import AdjustOutlined from "@mui/icons-material/AdjustOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import PendingOutlined from "@mui/icons-material/PendingOutlined";
import Schedule from "@mui/icons-material/Schedule";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  getActivityTitleOnly,
  getStaffFacingActivityTitle,
} from "encamp-shared/src/activity/activityDisplayNames";
import { ActivityStatus, ActivityType } from "generated-graphql/graphql";
import { NavLink } from "react-router-dom";

export interface Activity {
  id: string;
  type: ActivityType;
  status: ActivityStatus;
  description?: string | null;
  championId?: string | null;
}

export interface WorkflowActivityStepperProps {
  activities: Activity[];
  currentActivityId?: string | null;

  // Feature toggles (fulfillment has these, cohort doesn't need all)
  showTooltips?: boolean;
  showChampionIcon?: boolean;
  showSecondaryText?: boolean;
  showEmptyState?: boolean;

  // Customization options
  activityTitleFunction?: (activity: Activity) => string;
  championReportId?: string;
  maxHeight?: string;
  replace?: boolean; // Navigation replace behavior
  emptyStateMessage?: string;
}

// Helper functions for activity status
function getActivityStatusIcon(status: ActivityStatus) {
  const color = getActivityStatusColor(status);
  switch (status) {
    case ActivityStatus.StaffCompleted:
      return <Schedule color={color} />;
    case ActivityStatus.Completed:
      return <CheckCircleOutlineRounded color={color} />;
    case ActivityStatus.Error:
      return <ErrorOutlineRounded color={color} />;
    case ActivityStatus.InProgress:
      return <PendingOutlined color={color} />;
    default:
      return <AdjustOutlined color={color} />;
  }
}

function getActivityStatusColor(status: ActivityStatus) {
  switch (status) {
    case ActivityStatus.StaffCompleted:
      return "success" as const;
    case ActivityStatus.Completed:
      return "success" as const;
    case ActivityStatus.Error:
      return "error" as const;
    case ActivityStatus.InProgress:
      return "primary" as const;
    default:
      return "action" as const;
  }
}

// Champion activity types that show the champion icon
const CHAMPION_ACTIVITY_TYPES = [
  ActivityType.WaitForAllStateReports,
  ActivityType.WaitForAllCnReports,
  ActivityType.WaitForAllAltIdReports,
  ActivityType.WaitForAllCredentialReports,
  ActivityType.WaitForAllLepc,
  ActivityType.WaitForAllFd,
];

export function WorkflowActivityStepper({
  activities,
  currentActivityId,
  showTooltips = false,
  showChampionIcon = false,
  showSecondaryText = false,
  showEmptyState = false,
  activityTitleFunction = getActivityTitleOnly,
  championReportId,
  maxHeight = "500px",
  replace = false,
  emptyStateMessage = "No active workflow activities",
}: WorkflowActivityStepperProps) {
  const theme = useTheme();

  // Show empty state if enabled and no activities
  if (showEmptyState && activities.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyStateMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <List
      disablePadding
      sx={{
        maxHeight,
        overflowY: "auto",
      }}
    >
      {activities.map((activity) => {
        const activityTitle = activityTitleFunction(activity);
        const isChampion =
          showChampionIcon &&
          CHAMPION_ACTIVITY_TYPES.includes(activity.type) &&
          activity.championId === championReportId;

        const navLink = (
          <NavLink
            style={{ textDecoration: "none", color: "inherit" }}
            key={activity.id}
            to={activity.id}
            replace={replace}
          >
            {({ isActive }) => (
              <ListItem disablePadding sx={{ overflowX: "hidden" }}>
                <ListItemButton
                  sx={{
                    background: isActive ? theme.palette.action.selected : "inherit",
                    alignItems: "flex-start",
                    paddingLeft: theme.spacing(2),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: theme.spacing(1),
                      mt: theme.spacing(0.5),
                    }}
                  >
                    {getActivityStatusIcon(activity.status)}
                  </ListItemIcon>

                  {activity.id === currentActivityId && (
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mt: theme.spacing(0.5),
                        mr: theme.spacing(1),
                      }}
                    >
                      <ArrowForward color={getActivityStatusColor(activity.status)} />
                    </ListItemIcon>
                  )}

                  <ListItemText
                    primary={activityTitle}
                    secondary={showSecondaryText ? activity.description : undefined}
                  />

                  {isChampion && (
                    <ListItemIcon
                      sx={{
                        mt: theme.spacing(0.5),
                      }}
                    >
                      <EmojiEvents />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              </ListItem>
            )}
          </NavLink>
        );

        // Wrap in tooltip if enabled
        if (showTooltips) {
          return (
            <Tooltip key={activity.id} title={activityTitle} placement="right" enterDelay={1000}>
              {navLink}
            </Tooltip>
          );
        }

        return navLink;
      })}
    </List>
  );
}
